import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button as MuiButton, Menu, MenuItem } from "@mui/material";
import { HitStatus, SearchVersion } from "aderant-conflicts-models";
import { AderantTheme, ComboBoxItem } from "@aderant/aderant-react-components";
import { getHitStatusDisplayName } from "dataProviders/hitStatusList";
import { getHitStatusColors } from "Functions/getHitStatusColors";
import React, { useRef, useState } from "react";

export type HitStatusComboBoxProps = {
    id?: string;
    hitStatus: HitStatus;
    onItemSelected: (item: ComboBoxItem<HitStatus>) => void;
    searchVersion?: SearchVersion;
    style?: React.CSSProperties;
    className?: string;
    disabled?: boolean;
    hitStatusOptions: ComboBoxItem<HitStatus>[];
};

export function HitStatusComboBox(props: HitStatusComboBoxProps): JSX.Element {
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const muiButtonRef = useRef<HTMLButtonElement>(null);
    const { id, hitStatus, onItemSelected, searchVersion, style, className, disabled = false, hitStatusOptions } = props;
    //const hitStatusOptions = hitStatusList(permissions, searchVersion);
    const colors = getHitStatusColors(hitStatus);
    const handleSelected = (item: ComboBoxItem<HitStatus>): void => {
        if (onItemSelected) {
            onItemSelected(item);
        }
    };
    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const buttonWidth = muiButtonRef.current?.getBoundingClientRect().width;
    // Cache current hit status display name to avoid repeated function calls and remove need for any-cast
    const currentHitStatusDisplayName = getHitStatusDisplayName(hitStatus);

    const menuItems = hitStatusOptions.map((i) => {
        const selected = i.displayName === currentHitStatusDisplayName;
        return (
            <MenuItem
                key={i.displayName}
                selected={selected}
                onClick={() => {
                    handleClose();
                    handleSelected(i);
                }}
                style={{ minWidth: buttonWidth }}
            >
                {i.displayName}
            </MenuItem>
        );
    });

    const isOpen = !!anchorEl;
    const expandIcon = isOpen ? <ExpandLessIcon fontSize={"small"} /> : <ExpandMoreIcon fontSize={"small"} />;

    const buttonId: string | undefined = id ? `${id}-hitstatusbutton` : undefined;
    const menuId: string | undefined = id ? `${id}-hitstatusmenu` : undefined;

    return (
        <div id={id} className={className} style={{
            width: '100%',
            ...style
        }}>
            <MuiButton
                sx={{
                    ...colors.default,
                    width: '100%',
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    transitionDuration: "0s",
                    ":focus-visible": {
                        boxShadow: `0 0 0 1px ${colors.hover.borderColor}`,
                        ...colors.hover
                    },
                    "@media (hover: hover)": {
                        ":hover": colors.hover
                    },
                    "@media (hover: none)": {
                        ":hover": isOpen ? colors.clicked : colors.default
                    },
                    ...(disabled && { opacity: 0.4 }),
                    fontSize: AderantTheme.typography.pxToRem(12),
                    // Apply clicked styles with higher priority when isOpen is true
                    ...(isOpen && {
                        ...colors.clicked,
                        "&&": colors.clicked // Higher specificity for important styles
                    })
                }}
                onClick={handleClick}
                variant="outlined"
                ref={muiButtonRef}
                disableFocusRipple
                disableRipple
                endIcon={expandIcon}
                id={buttonId}
                aria-controls={menuId}
                aria-haspopup="menu"
                aria-expanded={isOpen ? "true" : "false"}
                disabled={disabled}
            >
                {currentHitStatusDisplayName}
            </MuiButton>
            <Menu
                id={menuId}
                aria-labelledby={buttonId}
                anchorEl={anchorEl}
                keepMounted
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
            >
                {menuItems}
            </Menu>
        </div>
    );
}

